// @ts-check

// CONSTANTS
/** @type {string} The API path for the post code data */
const API_PATH = 'https://xq4o-az4e-usmr.a2.xano.io/api:gDPHVMZA/post_codes/${postCode}'
/** @type {string} The class name for the loading state on the submit button */
const CLASS_SUBMIT_BUTTON_LOADING = 'pcpc__button--loading'

// Utility functions

/** async function that works similar to set timeout, but with a promise
 * @param {number} ms - The number of milliseconds to wait
 * @returns {Promise<void>} A promise that resolves after the given time
 * */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/** Gets component by name, following pattern of data-component-${name}
 * @param {string} name - The name of the component
 * @param {HTMLElement} parentElement - The parent element to search within
 * @returns {HTMLElement | null} The component element
 */
const getComponent = (name, parentElement = document.body) => {
  return parentElement.querySelector(`[data-component-${name}]`) || null
}

/** gets the variant selects custom element from the product page html
 * @param {string} productPageHtml - The html of the product page
 * @returns {HTMLElement | null} The variant selects custom element
 */
const getVariantSelectsCustomFromHtml = (productPageHtml) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(productPageHtml, 'text/html')
  return doc.querySelector('variant-selects-custom')
}

/** dispatches custom event on element, or document.body if no element is provided
 * @param {HTMLElement} targetElement - The target element to dispatch the event on
 * @param {string} name - The name of the event
 * @param {Object} detail - The detail object for the event
 */
const dispatchCustomEvent = (targetElement = document.body, name, detail = {}) => {
  const event = new CustomEvent(name, { detail })
  targetElement.dispatchEvent(event)
}

/** Fetches post code data from custom API
 * @param {number | null} postCode - The post code to fetch data for
 * @returns {Promise<Object>} The post code data
 */
const fetchPostCode = async (postCode) => {
  if (postCode === null) {
    throw new Error('Post code cannot be null')
  }
  const response = await fetch(API_PATH.replace('${postCode}', String(postCode)))
  return response.json()
}

/** fetches the html content for a page, specifically a product page with the html we want */
const fetchProductPage = async (productHandle) => {
  const response = await fetch(`${window.location.origin}/products/${productHandle}`)
  return response.text()
}

/** fetches the cart data from the cart.js endpoint */
const fetchCart = async () => {
  const response = await fetch(`${window.location.origin}/cart.js`)
  return response.json()
}

/** converts post from string to number, returns number or NaN
 * @param {string | null} postCode - The post code to validate
 * @returns {number | NaN} The validated post code or NaN
 */
const validatePostCode = (postCode) => {
  if (!postCode) return NaN
  return parseInt(postCode, 10)
}

class PostCodePickerCustom extends HTMLElement {
  constructor() {
    super()
    /** @type {HTMLFormElement | null} */
    this.form = null

    /** @type {HTMLInputElement | null} */
    this.input = null

    /** @type { number | null}*/
    this.postCode = null

    /** @type {HTMLDivElement | null} */
    this.errorMessage = null

    /** @type {HTMLElement | null} */
    this.productInfoCustom = null

    /** @type {ProductFormCustom | null} */
    this.productFormCustom = null

    /** @type {HTMLElement | null} */
    this.variantSelectsCustom = null

    /** @type {HTMLElement | null} */
    this.collectionOptionsCustom = null

    /** @type {HTMLElement | null} */
    this.deliveryOptionsCustom = null

    /** @type {HTMLElement | null} */
    this.clickAndCollectCustom = null

    /** @type {InStoreAccordion | null} */
    this.inStoreAccordion = null

    /** @type {HTMLElement | null} */
    this.shippingChargeProduct = null

    /** @type {HTMLButtonElement | null} */
    this.submitButton = null

    /** @type {HTMLSpanElement | null} */
    this.submitButtonTextElement = null

    /** @type {HTMLSpanElement | null} */
    this.submitButtonLoaderElement = null
  }

  connectedCallback() {
    this.setStaticHTMLElements()
    this.setEventListeners()
  }

  setEventListeners() {
    this.querySelector('[data-component-post-code-picker-form]')?.addEventListener(
      'submit',
      this.handleSubmit.bind(this)
    )
    /* remove disabled attribute from button on input change, or paste in */
    this.input?.addEventListener('input', () => {
      this.removeDisabledAttributeFromButton()
    })
  }

  setForm(form) {
    this.form = form
  }

  /** sets the input element
   * @param {HTMLInputElement | null} input
   */
  setInput(input) {
    this.input = input
  }

  /** sets the post code value
   * @param { number | null} postCode
   */
  setPostCode(postCode) {
    this.postCode = postCode
  }

  /** sets the product info custom element
   * @param {HTMLElement | null} productInfoCustom
   */
  setProductInfoCustom(productInfoCustom) {
    this.productInfoCustom = productInfoCustom
  }

  /** sets the product form custom element
   * @param {ProductFormCustom | null} productFormCustom
   */
  setProductFormCustom(productFormCustom) {
    this.productFormCustom = productFormCustom
  }

  /** sets the variant selects custom element
   * @param {HTMLElement | null} variantSelectsCustom
   */
  setVariantSelectsCustom(variantSelectsCustom) {
    this.variantSelectsCustom = variantSelectsCustom
  }

  /** sets the collection options custom element
   * @param {HTMLElement | null} collectionOptionsCustom
   */
  setCollectionOptionsCustom(collectionOptionsCustom) {
    this.collectionOptionsCustom = collectionOptionsCustom
  }

  /** sets the delivery options custom element
   * @param {HTMLElement | null} deliveryOptionsCustom
   */
  setDeliveryOptionsCustom(deliveryOptionsCustom) {
    this.deliveryOptionsCustom = deliveryOptionsCustom
  }

  /** sets the click and collect custom element
   * @param {HTMLElement | null} clickAndCollectCustom
   */
  setClickAndCollectCustom(clickAndCollectCustom) {
    this.clickAndCollectCustom = clickAndCollectCustom
  }

  /** sets the in store accordion element
   * @param {InStoreAccordion | null} inStoreAccordion
   */
  setInStoreAccordion(inStoreAccordion) {
    this.inStoreAccordion = inStoreAccordion
  }

  /** sets the shipping charge product element
   * @param {HTMLElement | null} shippingChargeProduct
   */
  setShippingChargeProduct(shippingChargeProduct) {
    this.shippingChargeProduct = shippingChargeProduct
  }

  /** sets the submit button
   * @param {HTMLButtonElement | null} submitButton
   */
  setSubmitButton(submitButton) {
    this.submitButton = submitButton
  }

  setSubmitButtonTextElement(submitButtonTextElement) {
    this.submitButtonTextElement = submitButtonTextElement
  }

  setSubmitButtonLoaderElement(submitButtonLoaderElement) {
    this.submitButtonLoaderElement = submitButtonLoaderElement
  }

  /** sets the error message element
   * @param {HTMLDivElement | null} errorMessage
   */
  setErrorMessage(errorMessage) {
    this.errorMessage = errorMessage
  }

  /** shows the error message */
  showErrorMessage() {
    this.errorMessage?.classList?.remove('hidden')
  }

  /** hides the error message */
  hideErrorMessage() {
    this.errorMessage?.classList?.add('hidden')
    this.input?.removeEventListener('input', this.showErrorMessage.bind(this))
  }

  setStaticHTMLElements() {
    const inputElement = getComponent('post-code-picker-input')
    if (inputElement instanceof HTMLInputElement) {
      this.setInput(inputElement)
    } else {
      this.setInput(null)
    }

    const errorMessageElement = getComponent('post-code-picker-error-message')
    if (errorMessageElement instanceof HTMLDivElement) {
      this.setErrorMessage(errorMessageElement)
    } else {
      this.setErrorMessage(null)
    }

    const productInfoCustom = document.body.querySelector('product-info-custom')
    if (productInfoCustom instanceof HTMLElement) {
      this.setProductInfoCustom(productInfoCustom)
    } else {
      this.setProductInfoCustom(null)
    }

    const productFormCustom = document.body.querySelector('product-form-custom')
    if (productFormCustom instanceof HTMLElement) {
      this.setProductFormCustom(productFormCustom)
    } else {
      this.setProductFormCustom(null)
    }

    const variantSelectsCustom = document.body.querySelector('variant-selects-custom')
    if (variantSelectsCustom instanceof HTMLElement) {
      this.setVariantSelectsCustom(variantSelectsCustom)
    } else {
      this.setVariantSelectsCustom(null)
    }

    const collectionOptionsCustom = document.body.querySelector('product-collection-options-custom')
    if (collectionOptionsCustom instanceof HTMLElement) {
      this.setCollectionOptionsCustom(collectionOptionsCustom)
    } else {
      this.setCollectionOptionsCustom(null)
    }

    const deliveryOptionsCustom = document.body.querySelector('product-delivery-options-custom')
    if (deliveryOptionsCustom instanceof HTMLElement) {
      this.setDeliveryOptionsCustom(deliveryOptionsCustom)
    } else {
      this.setDeliveryOptionsCustom(null)
    }

    const clickAndCollectCustom = document.body.querySelector('product-click-and-collect-custom')
    if (clickAndCollectCustom instanceof HTMLElement) {
      this.setClickAndCollectCustom(clickAndCollectCustom)
    } else {
      this.setClickAndCollectCustom(null)
    }

    const inStoreAccordion = document.body.querySelector('in-store-accordion')
    if (inStoreAccordion instanceof HTMLElement) {
      this.setInStoreAccordion(inStoreAccordion)
    } else {
      this.setInStoreAccordion(null)
    }

    const shippingChargeProduct = document.body.querySelector('product-shipping-charge-custom')
    if (shippingChargeProduct instanceof HTMLElement) {
      this.setShippingChargeProduct(shippingChargeProduct)
    } else {
      this.setShippingChargeProduct(null)
    }

    const submitButton = this.querySelector('[data-component-post-code-picker-button]')
    if (submitButton instanceof HTMLButtonElement) {
      this.setSubmitButton(submitButton)
    } else {
      this.setSubmitButton(null)
    }

    const submitButtonTextElement = this.querySelector('[data-component-post-code-picker-button-text]')
    if (submitButtonTextElement instanceof HTMLSpanElement) {
      this.setSubmitButtonTextElement(submitButtonTextElement)
    } else {
      this.submitButtonTextElement = null
    }

    const submitButtonLoaderElement = this.querySelector('[data-component-post-code-picker-button-loader]')
    if (submitButtonLoaderElement instanceof HTMLSpanElement) {
      this.setSubmitButtonLoaderElement(submitButtonLoaderElement)
    } else {
      this.submitButtonLoaderElement = null
    }
  }

  /** adds the disabled attribute to the submit button */
  addDisabledAttributeToButton() {
    this.submitButton?.setAttribute('disabled', 'disabled')
  }

  /** Adds the loading state class to the button */
  addLoadingStateToButton() {
    /** @type {number} */
    const width = this.submitButton?.offsetWidth ?? 0

    if (width) {
      this.submitButton?.style.setProperty('width', `${width}px`)
    }

    this.submitButton?.classList.add(CLASS_SUBMIT_BUTTON_LOADING)
  }

  /** removes the disabled attribute from the submit button */
  removeDisabledAttributeFromButton() {
    this.submitButton?.removeAttribute('disabled')
  }

  /** removes the loading state class from the button */
  removeLoadingStateFromButton() {
    this.submitButton?.style.removeProperty('width')
    this.submitButton?.classList.remove(CLASS_SUBMIT_BUTTON_LOADING)
  }

  async handleSubmit(event) {
    event.preventDefault()

    this.setForm(event.target)
    this.setStaticHTMLElements()

    try {
      /** @type {string | null} */
      const value = this?.input?.value ?? null

      /** @type {number | null} */
      this.postCode = validatePostCode(value)

      if (isNaN(this.postCode)) {
        throw new Error('Invalid post code')
      }

      this.addLoadingStateToButton()
      this.addDisabledAttributeToButton()

      /** @type {ApiResponse} */
      const postCodeData = await fetchPostCode(this.postCode)

      this.removeDisabledAttributeFromButton()
      this.removeLoadingStateFromButton()

      /** @type {CartResponse} */
      const cart = await fetchCart()

      /** @type {boolean} */
      const cartIsNotEmpty = cart.item_count > 0

      /** @type {boolean} */
      const hasClickAndCollectAttribute = cart.attributes['Shipping method'] === 'Click and Collect'

      /** @type {boolean} */
      const hasDeliveryAttribute = cart.attributes['Shipping method'] === 'Delivery'

      if ('code' in postCodeData) {
        throw new Error(postCodeData.message || 'An error occurred')
      }

      /** @type {DeliveryRoute} */
      const deliveryRoutes = postCodeData.Delivery_Routes

      /** @type {RetailLocation[]} */
      const retailLocations = postCodeData.Retail_Locations

      /** @type {string | null} */
      const locationURL = postCodeData.Region?.Location_URL

      /** @type {Region} */
      const region = postCodeData.Region

      /** @type {boolean} */
      const clickAndCollectAvailable = region?.Click_Collect_Available

      /** @type {number | null} */
      const closestRetailLocationId = postCodeData.Post_Code.retail_locations_id

      /** @type {boolean} */
      const hasDelivery = postCodeData.Post_Code.Delivery

      // Update the base product variant picker
      const productHandle = region?.Product_handle
      const productPageHtml = await fetchProductPage(productHandle)
      const existingVariantSelectsCustom = this.variantSelectsCustom
      const productSelectsCustomNew = getVariantSelectsCustomFromHtml(productPageHtml)

      const waitTime = 1000

      if (existingVariantSelectsCustom) {
        await new Promise((resolve) => {
          existingVariantSelectsCustom.animateClose(waitTime)
          setTimeout(resolve, waitTime)
        })

        existingVariantSelectsCustom.replaceWith(productSelectsCustomNew)
      }

      this.setVariantSelectsCustom(productSelectsCustomNew)
      this.variantSelectsCustom?.animateOpen(waitTime)

      /** @type {string} */
      const shippingChargeVariantId = deliveryRoutes?.Delivery_charge_variantId

      this.productInfoCustom?.showBaseProductOptions()
      this.productFormCustom?.show()

      this.setStaticHTMLElements()

      this.clickAndCollectCustom?.createStoreSelectElements(retailLocations)

      if (this.inStoreAccordion) {
        this.inStoreAccordion.storeLink = locationURL
      }

      if (deliveryRoutes && hasDelivery) {
        this.deliveryOptionsCustom?.enable()
        this.shippingChargeProduct?.selectOption(shippingChargeVariantId)
        this.deliveryOptionsCustom?.createOptionElements(deliveryRoutes.Delivery_dates)
        this.deliveryOptionsCustom.deliveryChargePrice = deliveryRoutes.Delivery_charge
        this.collectionOptionsCustom?.enable()
      } else {
        this.collectionOptionsCustom?.disable()
        this.shippingChargeProduct?.selectDefaultOption()
        this.deliveryOptionsCustom.deliveryChargePrice = 0
        this.deliveryOptionsCustom?.disable()
      }

      if (clickAndCollectAvailable === false) {
        this.clickAndCollectCustom?.disable()
      }

      if (closestRetailLocationId) {
        this.clickAndCollectCustom.setSelectedStoreById(closestRetailLocationId)
      }

      // if has click and collect attribute, disable delivery, if has delivery attribute, disable click and collect
      if (cartIsNotEmpty) {
        if (hasClickAndCollectAttribute) {
          this.deliveryOptionsCustom?.disable()
        } else if (hasDeliveryAttribute) {
          this.clickAndCollectCustom?.disable()
        }
      }

      this.productFormCustom?.updateSubTotal()
    } catch (error) {
      console.error(error)

      this.productInfoCustom?.hideBaseProductOptions()
      this.productFormCustom?.hide()

      // toggle error
      this.showErrorMessage()

      // hide error message when user starts typing
      this.input?.addEventListener('input', this.hideErrorMessage.bind(this))
    }
  }
}

customElements.define('post-code-picker-custom', PostCodePickerCustom)

// Typedefs
/**
 * @typedef {Object} ApiError
 * @property {string} code - The error code indicating the type of error.
 * @property {string} message - A message describing the error.
 * @property {string} traceId - A unique identifier for tracing the error request.
 */

/**
 * @typedef {Object} PostCodeData
 * @property {Object} Post_Code - Details about the post code.
 * @property {number} Post_Code.id - The unique identifier of the post code.
 * @property {number} Post_Code.created_at - The creation timestamp.
 * @property {number} Post_Code.Post_Code - The post code value.
 * @property {string} Post_Code.Name - The name of the location.
 * @property {boolean} Post_Code.Delivery - Whether delivery is available for this post code.
 * @property {boolean} Post_Code.Retail - Whether retail is available for this post code.
 * @property {number} Post_Code.delivery_routes_id - The ID for the delivery route.
 * @property {number} Post_Code.regions_id - The region ID.
 * @property {number} Post_Code.retail_locations_id - The retail location ID.
 * @property {DeliveryRoute} Delivery_Routes - The delivery route details.
 * @property {RetailLocation[]} Retail_Locations - An array of retail locations.
 * @property {Region} Region - Region details.
 */

/**
 * @typedef {Object} Region
 * @property {number} id - The unique identifier of the region.
 * @property {number} created_at - The creation timestamp.
 * @property {string} Region - The name of the region (e.g., "Melbourne").
 * @property {string} Product_handle - The product handle associated with the region (e.g., "real-christmas-tree-melbourne").
 * @property {string} Location_URL - The URL for location-specific information for the region.
 * @property {boolean} Click_Collect_Available - if click and collect is available for the region.
 */

/**
 * @typedef {Object} DeliveryRoute
 * @property {number} id - The unique identifier of the delivery route.
 * @property {number} created_at - The creation timestamp of the route.
 * @property {number} regions_id - The ID of the associated region.
 * @property {string} Route_Name - The name of the delivery route (e.g., "Inner").
 * @property {string[]} Delivery_dates - Array of available delivery dates.
 * @property {number} Delivery_charge - The charge for delivery in the given area.
 * @property {string} Delivery_charge_handle - The handle for the delivery charge (e.g., "shipping-charge").
 * @property {string} Delivery_charge_variantId - The variant ID for the delivery charge.
 */

/**
 * @typedef {Object} RetailLocation
 * @property {number} id - The unique identifier of the retail location.
 * @property {number} created_at - The timestamp for creation of the retail location.
 * @property {number} regions_id - The ID of the associated region.
 * @property {string} Name - The name of the retail location (e.g., "Preston").
 * @property {string[]} Opening_Dates - Array of available opening dates for the retail location.
 * @property {string} Address - The address of the retail location.
 * @property {string} Google_maps_url - URL to Google Maps for the retail location.
 * @property {string} phone - The phone number for the retail location (empty string if not available).
 * @property {string} Google_Review_URL - URL to Google reviews for the retail location (empty string if not available).
 */

/**
 * @typedef {Object} CartItem
 * @property {number} id - The unique identifier of the cart item.
 * @property {Object} properties - Additional properties of the item.
 * @property {number} quantity - The quantity of the item in the cart.
 * @property {number} variant_id - The ID of the product variant.
 * @property {string} key - A unique key for the cart item.
 * @property {string} title - The title of the item.
 * @property {number} price - The price of the item.
 * @property {number} original_price - The original price of the item.
 * @property {number} discounted_price - The discounted price of the item.
 * @property {number} line_price - The total price for this line item.
 * @property {number} original_line_price - The original total price for this line item.
 * @property {number} total_discount - The total discount applied to this item.
 * @property {Array} discounts - An array of discounts applied to this item.
 * @property {string|null} sku - The SKU of the item.
 * @property {number} grams - The weight of the item in grams.
 * @property {string} vendor - The vendor of the item.
 * @property {boolean} taxable - Whether the item is taxable.
 * @property {number} product_id - The ID of the product.
 * @property {boolean} product_has_only_default_variant - Whether the product has only the default variant.
 * @property {boolean} gift_card - Whether the item is a gift card.
 * @property {number} final_price - The final price of the item.
 * @property {number} final_line_price - The final total price for this line item.
 * @property {string} url - The URL of the product page.
 * @property {Object} featured_image - The featured image of the item.
 * @property {string|null} image - The URL of the item's image.
 * @property {string} handle - The handle of the product.
 * @property {boolean} requires_shipping - Whether the item requires shipping.
 * @property {string} product_type - The type of the product.
 * @property {string} product_title - The title of the product.
 * @property {string|null} product_description - The description of the product.
 * @property {string|null} variant_title - The title of the variant.
 * @property {string[]} variant_options - An array of variant options.
 * @property {Array<{name: string, value: string}>} options_with_values - An array of option objects with names and values.
 * @property {Array} line_level_discount_allocations - An array of line-level discount allocations.
 * @property {number} line_level_total_discount - The total line-level discount.
 * @property {boolean} has_components - Whether the item has components.
 */

/**
 * @typedef {Object} CartResponse
 * @property {string} token - The cart token.
 * @property {string} note - Any note attached to the cart.
 * @property {Object} attributes - Custom attributes for the cart.
 * @property {number} original_total_price - The original total price of the cart.
 * @property {number} total_price - The current total price of the cart.
 * @property {number} total_discount - The total discount applied to the cart.
 * @property {number} total_weight - The total weight of the cart items.
 * @property {number} item_count - The total number of items in the cart.
 * @property {CartItem[]} items - An array of items in the cart.
 * @property {boolean} requires_shipping - Whether the cart requires shipping.
 * @property {string} currency - The currency code.
 * @property {number} items_subtotal_price - The subtotal price of all items.
 * @property {Array} cart_level_discount_applications - An array of cart-level discount applications.
 */

/**
 * @typedef {PostCodeData | ApiError} ApiResponse
 */
